import { useLocalSearchParams, useRouter } from 'expo-router';
import { useFormContext, useWatch } from 'react-hook-form';
import { StyleSheet } from 'react-native-unistyles';
import { useQuery, useSubscription } from 'urql';
import { v4 } from 'uuid';
import { calculateElapsedMs } from '@/components/Event/EventClock/EventClock.utils';
import Box from '@/components/UI/Box';
import {
  type FragmentOf,
  graphql,
  type ResultOf,
  readFragment,
} from '@/gql/graphql';
import type { EventScorekeepingPanelFormValues } from '../../EventScorekeeping.types';
import EventScorekeepingPanelPerson, {
  EventScorekeepingPanelPersonFragment,
} from './EventScorekeepingPanelTeamPerson';

const PERSON_LIMIT = 100;

export const EventScorekeepingPanelTeamPeopleFragment = graphql(
  /* GraphQL */ `
    fragment EventScorekeepingPanelTeamPeople on event {
      id
      event_clock {
        event_id
        start_datetime
        elapsed_segment_ms
        is_running
        event_segment {
          id
          title
          duration
          duration_offset
          is_duration_capped
        }
      }
      event_segment_group {
        id
        direction
      }
    }
  `,
);

const GetEventScorekeepingPanelTeamPeople = graphql(/* GraphQL */ `
  query GetEventScorekeepingPanelTeamPeople($teamId: uuid!, $limit: Int) {
    badge(
      where: { title: { _eq: "Jersey Number" }, is_official: { _eq: true } }
    ) {
      id
    }
    person_team(
      where: { team_id: { _eq: $teamId } }
      order_by: { person: { name: asc } }
      limit: $limit
    ) {
      person_id
      team_id
      person {
        id
        name
        avatar_id
        color
        jersey_number(args: { team_id: $teamId })
        ...EventScorekeepingPanelPerson
      }
      badge_assignments(
        where: {
          badge: { title: { _eq: "Jersey Number" }, is_official: { _eq: true } }
        }
        limit: 1
      ) {
        id
        badge_assignee_attributes(limit: 1) {
          id
          value
        }
      }
    }
  }
`, [EventScorekeepingPanelPersonFragment]);

const WatchEventScorekeepingPanelTeamPeople = graphql(/* GraphQL */ `
  subscription WatchEventScorekeepingPanelTeamPeople(
    $teamId: uuid!
    $limit: Int
  ) {
    person_team(
      where: { team_id: { _eq: $teamId } }
      order_by: { person: { name: asc } }
      limit: $limit
    ) {
      person_id
      team_id
      person {
        id
        name
        avatar_id
        color
        jersey_number(args: { team_id: $teamId })
        ...EventScorekeepingPanelPerson
      }
      badge_assignments(
        where: {
          badge: { title: { _eq: "Jersey Number" }, is_official: { _eq: true } }
        }
        limit: 1
      ) {
        id
        badge_assignee_attributes(limit: 1) {
          id
          value
        }
      }
    }
  }
`, [EventScorekeepingPanelPersonFragment]);

function EventScorekeepingPanelTeamPeople({
  fragment,
  teamId,
}: {
  fragment:
    | FragmentOf<typeof EventScorekeepingPanelTeamPeopleFragment>
    | null
    | undefined;
  teamId: string;
}) {
  const { navigate } = useRouter();

  const { badgeAssigneeId } = useLocalSearchParams<{
    badgeAssigneeId?: string;
  }>();

  const { getValues, setValue, control } =
    useFormContext<EventScorekeepingPanelFormValues>();

  const isEditing = useWatch({
    control,
    name: 'isEditing',
  });

  const selectedPerson = useWatch({
    control,
    name: 'selectedPerson',
  });

  const [{ data }] = useQuery({
    query: GetEventScorekeepingPanelTeamPeople,
    variables: {
      teamId,
      limit: PERSON_LIMIT,
    },
  });

  useSubscription({
    query: WatchEventScorekeepingPanelTeamPeople,
    variables: {
      teamId,
      limit: PERSON_LIMIT,
    },
  });

  const onPressPerson = (
    teamPerson: ResultOf<
      typeof GetEventScorekeepingPanelTeamPeople
    >['person_team'][0],
    isEditing: boolean,
  ) => {
    const event = readFragment(
      EventScorekeepingPanelTeamPeopleFragment,
      fragment,
    );

    const selectedTeam = getValues('selectedTeam');

    if (isEditing) {
      const jerseyNumberAssigneeId = teamPerson.badge_assignments?.[0]?.id;
      const jerseyNumberBadgeId = data?.badge[0]?.id;

      if (!jerseyNumberBadgeId) {
        return;
      }

      if (!jerseyNumberAssigneeId) {
        navigate({
          pathname: '/badge-assignee/create',
          params: {
            badgeId: jerseyNumberBadgeId,
            teamId: teamPerson.team_id,
            personId: teamPerson.person_id,
          },
        });
        return;
      }

      navigate({
        pathname: '/badge-assignee/[assigneeId]/edit',
        params: {
          assigneeId: jerseyNumberAssigneeId,
          teamId: teamPerson.team_id,
          personId: teamPerson.person_id,
        },
      });
      return;
    }

    const selectedPerson = getValues('selectedPerson');

    if (teamPerson.person_id === selectedPerson?.id) {
      setValue('selectedPerson', null);
      return;
    }

    const selectedBadge = getValues('selectedBadge');

    const recipients = getValues('recipients');
    const badgeAttributes = selectedBadge?.badge_attributes;

    if (
      !recipients.length &&
      !badgeAssigneeId &&
      !selectedPerson &&
      !selectedBadge
    ) {
      const calculatedElapsedMs = calculateElapsedMs({
        isRunning: event?.event_clock?.is_running ?? false,
        startDatetime: event?.event_clock?.start_datetime ?? null,
        elapsedSegmentMs: event?.event_clock?.elapsed_segment_ms ?? 0,
        isDurationCapped:
          event?.event_clock?.event_segment?.is_duration_capped ?? false,
        duration: event?.event_clock?.event_segment?.duration ?? 0,
      });

      if (event?.event_clock?.event_segment) {
        setValue('selectedTime', {
          elapsed_segment_ms: calculatedElapsedMs,
          event_segment: event?.event_clock.event_segment,
        });
      }
    }

    if (selectedBadge?.id && teamPerson && selectedTeam) {
      const newRecipient = {
        id: v4(),
        badge: selectedBadge,
        badge_assignee_attributes:
          badgeAttributes?.map((attribute) => {
            return {
              id: v4(),
              badge_attribute: attribute,
              value: attribute.default_value,
            };
          }) || [],
        person_team: {
          person_id: teamPerson.person_id,
          team_id: teamPerson.team_id,
          jersey_number: teamPerson.person?.jersey_number ?? null,
          person: teamPerson.person,
          team: selectedTeam,
        },
        isDeleted: false,
        isNew: true,
      };

      setValue('selectedBadge', null);

      setValue('recipients', [...recipients, newRecipient], {
        shouldDirty: true,
      });

      if (selectedBadge.can_quick_assign) {
        setValue('selectedRecipientId', newRecipient.id, {
          shouldDirty: true,
        });
      }

      return;
    }

    setValue('selectedPerson', teamPerson.person);
  };

  return (
    <Box
      flex
      direction="horizontal"
      alignItems="flex-start"
      justifyContent="flex-start"
      style={style.container}
      space="md"
    >
      {data?.person_team?.map((teamPerson) => {
        const jerseyNumber =
          teamPerson.badge_assignments?.[0]?.badge_assignee_attributes?.[0]
            ?.value ?? null;

        return (
          <EventScorekeepingPanelPerson
            key={teamPerson.person_id}
            fragment={teamPerson.person}
            isChecked={teamPerson.person_id === selectedPerson?.id}
            isEditing={isEditing}
            onPress={() => onPressPerson(teamPerson, isEditing)}
            jerseyNumber={jerseyNumber}
          />
        );
      })}
    </Box>
  );
}

const style = StyleSheet.create(() => ({
  container: {
    flexWrap: 'wrap',
  },
}));

export default EventScorekeepingPanelTeamPeople;
